import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { mockData } from '../data/mock';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Lignages = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const { currentLanguage } = useLanguage();
  
  // Savoir si des traductions sont en cours
  const isTranslating = useTranslationStatus();

  // Traductions des textes statiques
  const { translatedText: titleTranslated } = useTranslate("Histoire des Lignages");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: descTranslated } = useTranslate("Découvrez l'histoire fascinante des sept lignages nobles de Bruxelles");
  
  // Traduction du contenu dynamique - SEULEMENT si pageData.content existe
  const { translatedText: contentTranslated } = useTranslate(pageData?.content || "");

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site/pages/lignages`);
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      }
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Image Banner */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') 
              ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`)
              : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      {/* Dynamic Content from Database */}
      <div className="py-16" style={{ paddingTop: pageData?.header_image ? '4rem' : '8rem' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alerte de traduction en cours - SOUS LE HEADER avec z-index élevé */}
          {isTranslating && currentLanguage !== 'fr' && (
            <div 
              className="mb-6 bg-blue-50 border-l-4 border-blue-600 text-blue-800 px-6 py-4 rounded-r-lg shadow-md flex items-center gap-3"
              style={{ fontFamily: '"Lora", serif', position: 'relative', zIndex: 10 }}
            >
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="font-semibold">Traduction en cours...</p>
                <p className="text-sm">Le contenu s'affiche en français en attendant la traduction complète.</p>
              </div>
            </div>
          )}
          
          {!pageData?.header_image && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
              <p 
                className="text-gray-600"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {descTranslated}
              </p>
            </div>
          )}

          {/* Affichage du contenu dynamique depuis la DB */}
          {pageData?.content && (
            <div 
              dangerouslySetInnerHTML={{ 
                __html: currentLanguage === 'fr' 
                  ? pageData.content 
                  : (contentTranslated || pageData.content) 
              }}
              style={{ fontFamily: '"Lora", serif' }}
            />
          )}
          
          {/* Si pas de contenu, afficher un message */}
          {!pageData?.content && !loading && (
            <div className="text-center text-gray-500 py-12">
              <p style={{ fontFamily: '"Lora", serif' }}>Aucun contenu disponible pour cette page.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lignages;
